import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import LandingSplash from "@/components/LandingSplash";
import {
  ArrowRight, Search, TrendingUp, MapPin, ShieldCheck, Megaphone, Users,
  Building2, Sparkles, Layers, Home as HomeIcon, Briefcase, MessageCircle, Phone, Instagram,
} from "lucide-react";
import { resolveHeroImage, NoImagePlaceholder, formatPrice } from "@/components/ProjectCard";
import ProjectCard from "@/components/ProjectCard";

// ─── Motion primitives ──────────────────────────────────────────────────
// Single reveal preset used across sections so every section shares the
// exact same choreography — restraint over dazzle.
const revealVariants = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const staggerParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.08 } },
};

function Reveal({ children, className, delay = 0, ...rest }) {
  return (
    <motion.div
      variants={revealVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// Premium editorial hero — a dusk-lit modern residential tower shot by
// Alicia Steels (Unsplash). Sized to 1920w q=75 ≈ 720 KB — crisp on retina
// desktops without blowing the LCP budget. Swap the slug here if a better
// Pune / India-specific shot lands.
// Hero backgrounds — audience-specific.
// Buyer/renter surface: warm luxury interior (Unsplash).
// Real-estate professional surface: modern aerial cityscape (Unsplash).
const HERO_IMAGE_BUYER =
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1920&q=75";
const HERO_IMAGE_AGENT =
  "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=1920&q=75";

// ─── Live Pulse Rail ─────────────────────────────────────────────────────
// Vertical activity feed — fixed on the right on desktop only. Populated
// from lightweight recent-listings + recent-projects endpoints already used
// elsewhere. Falls back silently on empty state.
function LivePulseRail() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [buy, projects] = await Promise.all([
          api.get("/buy", { params: { limit: 6 } }),
          api.get("/master-projects", { params: { limit: 6 } }),
        ]);
        const feed = [];
        (buy?.data?.items || []).slice(0, 3).forEach((it) => {
          feed.push({
            kind: "listing",
            label: `New in ${it.locality || "Pune"}`,
            detail: `${it.bhk || it.property_type || "Unit"} · ${it.price_lakhs ? formatPrice(it.price_lakhs) : "On request"}`,
          });
        });
        (projects?.data?.projects || projects?.data || []).slice(0, 3).forEach((p) => {
          feed.push({
            kind: "rera",
            label: `RERA verified · ${p.maharera_id || p.project_id}`,
            detail: `${p.project_name} · ${p.locality_tag || p.zone || ""}`,
          });
        });
        [
          { kind: "market", label: "Market +2.1% · Kharadi", detail: "MoM · avg ₹/sqft" },
          { kind: "market", label: "Coverage 94% · Pune West", detail: "Verified corridors" },
        ].forEach((m) => feed.push(m));
        setItems(feed.slice(0, 8));
      } catch (e) {
        /* silent */
      }
    })();
  }, []);

  const dotColor = {
    listing: "bg-emerald-500",
    rera: "bg-sg-gold",
    market: "bg-sky-500",
  };

  if (!items.length) return null;
  return (
    <aside
      className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40 w-64"
      data-testid="landing-live-pulse"
    >
      <div className="bg-white/85 backdrop-blur-xl border border-sg-line rounded-2xl shadow-lg shadow-sg-navy/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-sg-navy font-semibold">Live Pulse</span>
          </div>
          <span className="font-mono text-[10px] text-sg-mute">LIVE</span>
        </div>
        <ul className="space-y-2.5 max-h-[420px] overflow-hidden">
          {items.map((it, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="flex items-start gap-2.5 text-[11px]"
            >
              <span className={`mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full ${dotColor[it.kind] || "bg-sg-navy"}`} />
              <div className="min-w-0">
                <div className="text-sg-navy font-semibold truncate">{it.label}</div>
                <div className="text-sg-mute truncate leading-relaxed">{it.detail}</div>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

// ─── Micro-stat inline (grey, monospace) ────────────────────────────────
function MicroStat({ label, value }) {
  return (
    <div className="inline-flex items-baseline gap-1.5 font-mono text-[10px] text-sg-mute uppercase tracking-[0.14em]">
      <span>{label}</span>
      <span className="text-sg-navy/70 font-semibold">{value}</span>
    </div>
  );
}

// ─── Agent directory card (agent audience rail) ─────────────────────────
// Resolves relative /api/files/branding/... URLs so `<img>` works whether
// the frontend is on preview, prod, or a local proxy.
const resolveAgentPhoto = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
};

function AgentDirectoryCard({ agent }) {
  const photo = resolveAgentPhoto(agent.picture);
  const initials = (agent.name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0].toUpperCase()).join("") || "A";
  const phoneDigits = (agent.phone || "").replace(/[^0-9]/g, "");
  const waMsg = `Hi ${agent.first_name}, saw your profile on INPropPortal. Can we chat about ${agent.primary_locality || "Pune"} properties?`;
  const waHref = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waMsg)}`
    : null;

  return (
    <div
      className="group relative bg-white border border-sg-line rounded-2xl p-5 flex items-center gap-4 hover:border-sg-gold-deep hover:shadow-lg hover:shadow-sg-gold/5 transition-all"
      data-testid={`landing-agent-card-${agent.slug}`}
    >
      {/* Avatar */}
      <Link to={`/agent/${agent.slug}`} className="flex-shrink-0" aria-label={`View ${agent.name}'s portal`}>
        {photo ? (
          <img
            src={photo}
            alt={agent.name}
            className="w-20 h-20 rounded-2xl object-cover border border-sg-line"
            data-testid={`landing-agent-photo-${agent.slug}`}
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = "flex"); }}
          />
        ) : null}
        <div
          className={`w-20 h-20 rounded-2xl bg-sg-cream-soft border border-sg-line ${photo ? "hidden" : "flex"} items-center justify-center font-hero text-2xl text-sg-navy font-semibold`}
          style={{ display: photo ? "none" : "flex" }}
          data-testid={`landing-agent-initials-${agent.slug}`}
        >
          {initials}
        </div>
      </Link>

      {/* Meta */}
      <div className="flex-1 min-w-0">
        <Link to={`/agent/${agent.slug}`} className="block group-hover:text-sg-gold-deep">
          <div className="font-hero text-2xl text-sg-navy leading-tight truncate">{agent.first_name}</div>
        </Link>
        <div className="mt-1 text-xs text-sg-mute flex items-center gap-1 truncate">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          {agent.primary_locality || "Pune"}
          {agent.micro_market ? <span className="text-sg-line">·</span> : null}
          {agent.micro_market ? <span className="truncate">{agent.micro_market}</span> : null}
        </div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.12em] text-sg-gold-deep">
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        </div>
      </div>

      {/* CTA */}
      <div className="flex-shrink-0">
        {waHref ? (
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-sg-navy hover:bg-sg-navy-700 text-white text-xs font-semibold whitespace-nowrap transition-colors"
            data-testid={`landing-agent-contact-btn-${agent.slug}`}
          >
            <MessageCircle className="w-4 h-4" /> Contact {agent.first_name}
          </a>
        ) : (
          <a
            href={`tel:${agent.phone}`}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-sg-navy hover:bg-sg-navy-700 text-white text-xs font-semibold whitespace-nowrap transition-colors"
            data-testid={`landing-agent-contact-btn-${agent.slug}`}
          >
            <Phone className="w-4 h-4" /> Call {agent.first_name}
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Interactive Launch Roadmap ─────────────────────────────────────────
const ROADMAP = [
  { city: "Pune",      status: "live",   window: "Live now",   detail: "3+ verified corridors, active advisors, growing inventory." },
  { city: "Mumbai",    status: "next",   window: "Q4 2026",    detail: "Onboarding MahaRERA-badged advisors for Bandra, Andheri, Powai." },
  { city: "Bengaluru", status: "soon",   window: "Q1 2027",    detail: "KRERA compliance ready — Whitefield, HSR, ORR corridors first." },
  { city: "Delhi NCR", status: "soon",   window: "Q2 2027",    detail: "RERA-verified advisors across Gurugram, Noida, Greater Noida." },
  { city: "Hyderabad", status: "soon",   window: "Q2 2027",    detail: "Financial District, Kokapet, Gachibowli launch cluster." },
  { city: "Chennai",   status: "soon",   window: "Q3 2027",    detail: "OMR + ECR corridors led by TN-RERA authorised partners." },
  { city: "Ahmedabad", status: "future", window: "Q3 2027",    detail: "SG Highway + Bopal launch — Gujarat-RERA compliant onboarding." },
  { city: "Chandigarh",status: "future", window: "Q4 2027",    detail: "Tri-city launch (Chandigarh · Mohali · Panchkula)." },
];

function LaunchRoadmap() {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start end", "end start"],
  });
  // Animated progress fill — driven by scroll position across the section.
  const progress = useTransform(scrollYProgress, [0.05, 0.6], ["0%", "100%"]);

  return (
    <div ref={trackRef} className="relative">
      {/* Timeline rail */}
      <div className="relative" data-testid="landing-city-strip">
        <div className="absolute inset-x-0 top-6 h-px bg-sg-line" />
        <motion.div
          className="absolute left-0 top-6 h-px bg-gradient-to-r from-sg-gold-deep via-sg-gold to-sg-gold/40"
          style={{ width: progress }}
        />

        <div className="relative grid grid-cols-4 md:grid-cols-8 gap-3">
          {ROADMAP.map((c, i) => {
            const isLive = c.status === "live";
            const isActive = active === i;
            return (
              <button
                type="button"
                key={c.city}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className="group flex flex-col items-center text-center focus:outline-none"
                data-testid={`landing-city-${c.city.toLowerCase().replace(/\s/g, "-")}`}
              >
                {/* Node */}
                <div className="relative">
                  {isLive && (
                    <span className="absolute inset-0 rounded-full bg-sg-gold animate-ping opacity-40" />
                  )}
                  <span
                    className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${
                      isLive
                        ? "bg-sg-gold border-sg-gold shadow-[0_0_20px_rgba(201,162,74,0.4)]"
                        : isActive
                        ? "bg-white border-sg-gold-deep"
                        : "bg-white border-sg-line group-hover:border-sg-gold/60"
                    }`}
                  >
                    <MapPin
                      className={`w-4 h-4 ${
                        isLive ? "text-sg-navy" : isActive ? "text-sg-gold-deep" : "text-sg-mute group-hover:text-sg-gold-deep"
                      }`}
                    />
                  </span>
                </div>
                {/* Label */}
                <div
                  className={`mt-3 text-xs font-semibold transition-colors ${
                    isLive || isActive ? "text-sg-navy" : "text-sg-mute group-hover:text-sg-navy"
                  }`}
                >
                  {c.city}
                </div>
                <div className={`text-[10px] font-mono uppercase tracking-[0.14em] ${
                  isLive ? "text-sg-gold-deep font-semibold" : "text-sg-mute"
                }`}>
                  {c.window}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail card — animated crossfade on hover / click */}
      <div className="mt-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl bg-white border border-sg-line rounded-2xl p-6"
            data-testid="landing-roadmap-detail"
          >
            <div className="flex items-baseline justify-between">
              <div className="font-hero text-3xl text-sg-navy">{ROADMAP[active].city}</div>
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-sg-gold-deep">
                {ROADMAP[active].window}
              </div>
            </div>
            <div className="mt-2 h-px bg-sg-gold/40 w-16" />
            <p className="mt-4 text-sm text-sg-mute leading-relaxed">
              {ROADMAP[active].detail}
            </p>
            {ROADMAP[active].status !== "live" && (
              <Link
                to="/register?role=agent"
                className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-sg-gold-deep hover:text-sg-navy"
              >
                Reserve your microsite in {ROADMAP[active].city} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────
const localitySlug = (l) => (l || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function LocalityChip({ trend, ariaHidden = false }) {
  return (
    <Link
      to={`/pune/${localitySlug(trend.locality)}`}
      className="group flex-shrink-0 w-72 bg-white rounded-2xl p-5 hover:shadow-lg hover:shadow-sg-gold/5 transition-all"
      style={{ border: "3px solid #B8960C" }}
      data-testid={`landing-locality-${trend.locality}`}
      aria-hidden={ariaHidden || undefined}
      tabIndex={ariaHidden ? -1 : undefined}
    >
      <div className="flex items-baseline justify-between">
        <div className="font-hero text-2xl text-sg-navy">{trend.locality}</div>
        <div className="font-mono text-sm text-sg-gold-deep">{trend.yoy}</div>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-sg-mute">Year-over-Year</div>
      <div className="mt-3 h-px bg-sg-gold/40" />
      <p className="mt-3 text-xs text-sg-mute leading-relaxed line-clamp-3">{trend.summary}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-sg-navy font-semibold">
          {trend.active_projects || 0} active project{trend.active_projects === 1 ? "" : "s"}
        </span>
        <ArrowRight className="w-4 h-4 text-sg-mute group-hover:text-sg-gold-deep transition-colors" />
      </div>
    </Link>
  );
}

function ProjectTile({ p }) {
  const hero = resolveHeroImage(p);
  const price = p.starting_price_lakhs != null ? formatPrice(p.starting_price_lakhs) : null;
  return (
    <Link
      to={`/project/${p.project_id}`}
      className="group flex flex-col bg-white border border-sg-line rounded-2xl overflow-hidden hover:border-sg-gold-deep hover:shadow-lg hover:shadow-sg-gold/5 transition-all"
      data-testid={`landing-project-${p.project_id}`}
    >
      <div className="relative aspect-[16/9] bg-sg-cream-soft overflow-hidden">
        {hero ? (
          <img
            src={hero}
            alt={p.project_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
        ) : (
          <NoImagePlaceholder />
        )}
        <div className="absolute top-3 left-3 bg-sg-navy/90 backdrop-blur-sm text-sg-gold text-[10px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 font-semibold">
          {p.locality_tag || p.zone || "Pune"}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="font-hero text-lg text-sg-navy leading-tight line-clamp-1">{p.project_name}</div>
        <div className="text-xs text-sg-mute mt-0.5 line-clamp-1">{p.developer || "TBD"}</div>
        <div className="mt-auto pt-3">
          <div className="h-px bg-sg-line" />
          <div className="mt-3 flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-sg-mute font-semibold">Starting</span>
            <span className="font-mono text-sm text-sg-gold-deep font-semibold">{price || "On request"}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ─── Bento cell wrapper ────────────────────────────────────────────────
function BentoCell({ children, className = "", accent = false, testId }) {
  return (
    <motion.div
      variants={revealVariants}
      className={`relative rounded-3xl border overflow-hidden ${
        accent
          ? "bg-sg-navy border-sg-navy text-white"
          : "bg-white border-sg-line text-sg-navy"
      } ${className}`}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// LANDING
// ═══════════════════════════════════════════════════════════════════════
export default function Landing() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({ projects_count: 0, localities_count: 10 });
  const [buyCount, setBuyCount] = useState(0);
  const [trends, setTrends] = useState([]);
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");

  // ─── Split hero controls ─────────────────────────────────────────
  const [audience, setAudience] = useState(() => {
    if (typeof window === "undefined") return "buyer";
    const saved = window.localStorage.getItem("inprop.landing.audience");
    return saved === "agent" ? "agent" : "buyer";
  });
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [locality, setLocality] = useState("");
  const [budget, setBudget] = useState("");   // encoded as "min-max" in lakhs, e.g. "100-200"
  const [bhk, setBhk] = useState("");
  const [intent, setIntent] = useState("buy"); // "buy" | "rent"

  // ─── Registered-agents rail (agent audience view) ────────────────
  // Cached in localStorage for 10 min so we don't hit the API on every
  // audience toggle. Refresh-count updates the tile in the insights band.
  const [agents, setAgents] = useState([]);
  const [agentsCount, setAgentsCount] = useState(null);
  const AGENTS_CACHE_KEY = "inprop.public.agents";
  const AGENTS_TTL_MS = 10 * 60 * 1000; // 10 min

  useEffect(() => {
    if (audience !== "agent") return;
    // Serve cached snapshot immediately if fresh.
    try {
      const raw = window.localStorage.getItem(AGENTS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (Date.now() - parsed.t) < AGENTS_TTL_MS) {
          setAgents(parsed.agents || []);
          setAgentsCount(parsed.count ?? (parsed.agents || []).length);
          return;
        }
      }
    } catch (_e) { /* corrupt cache — ignore */ }

    api.get("/public/agents")
      .then(({ data }) => {
        setAgents(data.agents || []);
        setAgentsCount(data.count ?? (data.agents || []).length);
        try {
          window.localStorage.setItem(AGENTS_CACHE_KEY, JSON.stringify({
            t: Date.now(),
            agents: data.agents || [],
            count: data.count ?? (data.agents || []).length,
          }));
        } catch (_e) { /* quota — ignore */ }
      })
      .catch(() => { setAgents([]); setAgentsCount(0); });
  }, [audience]);

  // Persist the toggle so returning visitors land in their last-picked mode.
  useEffect(() => {
    try {
      window.localStorage.setItem("inprop.landing.audience", audience);
    } catch (_e) { /* localStorage disabled — ignore */ }
  }, [audience]);

  useEffect(() => {
    api.get("/public-stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/localities/trends").then(({ data }) => setTrends(data.trends || [])).catch(() => {});
    api.get("/localities").then(({ data }) => setLocalities(data || {})).catch(() => {});
    api.get("/master-projects", { params: { limit: 6 } })
      .then(({ data }) => setProjects((data.projects || data || []).slice(0, 6)))
      .catch(() => {});
    api.get("/buy", { params: { limit: 1 } }).then(({ data }) => setBuyCount(data.total_count ?? data.count ?? 0)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const q = query.trim();
    if (q) params.set("q", q);
    if (locality) params.set("locality", locality);
    if (bhk) params.set("bhk", bhk);
    if (intent === "buy" && budget) {
      const [minL, maxL] = budget.split("-");
      if (minL) params.set("min_price", minL);
      if (maxL) params.set("max_price", maxL);
    }
    const qs = params.toString();
    const base = intent === "rent" ? "/rent" : "/buy";
    navigate(qs ? `${base}?${qs}` : base);
  };

  const allLocalities = [
    ...(localities["Pune West"] || []),
    ...(localities["Pune East"] || []),
  ];

  const BUDGETS = [
    { label: "Under ₹50 L", value: "0-50" },
    { label: "₹50 L – 1 Cr", value: "50-100" },
    { label: "₹1 – 2 Cr", value: "100-200" },
    { label: "₹2 – 5 Cr", value: "200-500" },
    { label: "₹5 Cr+", value: "500-" },
  ];
  const BHKS = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5+ BHK"];

  const topYoy = trends[0];
  const isAgentView = audience === "agent";

  // Average locality YoY across every tracked corridor. Each trend row's `yoy`
  // is a signed percent string like "+9.2%" — parse defensively and drop rows
  // we can't read so a single bad entry never zeroes the average.
  const avgLocalityYoy = useMemo(() => {
    const nums = trends
      .map((t) => parseFloat((t.yoy || "").replace(/[^\d.\-+]/g, "")))
      .filter((n) => Number.isFinite(n));
    if (!nums.length) return null;
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    const sign = avg >= 0 ? "+" : "";
    return `${sign}${avg.toFixed(1)}%`;
  }, [trends]);

  const insightsTiles = [
    {
      value: stats.agents_count != null ? stats.agents_count.toLocaleString("en-IN") : (agentsCount != null ? agentsCount.toLocaleString("en-IN") : "—"),
      label: "Verified agents",
      href: isAgentView ? "#landing-agents-grid" : "/buy",
    },
    { value: stats.projects_count || "—", label: "Verified projects", href: "/buy?property_source=master_projects" },
    { value: stats.new_projects_30d != null ? stats.new_projects_30d.toLocaleString("en-IN") : "—", label: "New this month", href: "/buy?property_source=master_projects" },
    { value: stats.avg_rental_yield_pct != null ? `${stats.avg_rental_yield_pct}%` : "—", label: "Avg rental yield", href: "/rent" },
    { value: `${stats.localities_count}+`, label: "Pune localities", href: "/buy" },
    { value: avgLocalityYoy || "—", label: "Avg locality YoY", href: "/buy" },
  ];

  return (
    <div className="min-h-screen bg-sg-paper">
      <LandingSplash />
      <PublicNav />
      <LivePulseRail />

      {/* ═══════════════ 1. IMMERSIVE HERO ═══════════════ */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="landing-hero">
        {/* Full-bleed background */}
        <div className="absolute inset-0">
          <img
            src={isAgentView ? HERO_IMAGE_AGENT : HERO_IMAGE_BUYER}
            alt={isAgentView ? "Aerial cityscape — modern skyline" : "Luxury apartment interior — warm premium living"}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {/* Softened navy gradient — brighter photo, still legible H1. */}
          <div className="absolute inset-0 bg-gradient-to-br from-sg-navy/70 via-sg-navy/40 to-sg-navy/20" />
          {/* Buyer-hero dark overlay — flat rgba(0,0,0,0.45) so white headline
              stays readable on the warm luxury-interior photo. */}
          {!isAgentView && (
            <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} />
          )}
          {/* Subtle vignette + gold shimmer top edge */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sg-gold to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(201,162,74,0.15),transparent_55%)]" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
            data-testid="landing-hero-buyer"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-md px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] text-sg-gold font-semibold">
              <Sparkles className="w-3 h-3" />
              India&apos;s Authorised Property Portal
            </div>

            <h1 className="mt-8 font-hero text-white text-[38px] sm:text-[52px] md:text-[72px] lg:text-[92px] leading-[0.95] font-semibold tracking-tight">
              Property in India,<br />
              <span className="font-hero-italic text-sg-gold font-normal">clearly</span> understood.
            </h1>

            <p className="mt-8 text-lg text-white/80 max-w-xl leading-relaxed font-light">
              One verified network of RERA-registered advisors and curated master projects.
              Live in Pune today, launching city by city through 2026.
            </p>

            {/* ─── PROMINENT AUDIENCE TOGGLE ─────────────────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex w-full max-w-md sm:w-auto sm:inline-flex items-stretch sm:items-center gap-1.5 rounded-3xl sm:rounded-full border border-white/25 bg-white/8 backdrop-blur-xl p-1.5 shadow-lg shadow-sg-navy/30"
              role="tablist"
              aria-label="Choose your experience"
              data-testid="landing-audience-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={audience === "buyer"}
                onClick={() => setAudience("buyer")}
                className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-full px-4 sm:px-5 md:px-6 h-11 text-xs sm:text-sm font-semibold whitespace-nowrap transition-all ${
                  audience === "buyer"
                    ? "bg-sg-gold text-sg-navy shadow-md shadow-sg-gold/40"
                    : "text-white/75 hover:text-white hover:bg-white/5"
                }`}
                data-testid="landing-audience-buyer"
              >
                <HomeIcon className="w-4 h-4" />
                <span className="sm:hidden">Buyer / Renter</span>
                <span className="hidden sm:inline">I&apos;m a Buyer / Renter</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={audience === "agent"}
                onClick={() => setAudience("agent")}
                className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-full px-4 sm:px-5 md:px-6 h-11 text-xs sm:text-sm font-semibold whitespace-nowrap transition-all ${
                  audience === "agent"
                    ? "bg-sg-gold text-sg-navy shadow-md shadow-sg-gold/40"
                    : "text-white/75 hover:text-white hover:bg-white/5"
                }`}
                data-testid="landing-audience-agent"
              >
                <Briefcase className="w-4 h-4" />
                <span className="sm:hidden">Professional</span>
                <span className="hidden sm:inline">Real Estate Professional</span>
              </button>
            </motion.div>

            {/* ─── SWITCHED PAYLOAD ─────────────────────────────────── */}
            <AnimatePresence mode="wait">
              {audience === "buyer" ? (
                <motion.form
                  key="buyer-search"
                  onSubmit={onSearch}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-6 max-w-3xl"
                  data-testid="landing-search-form"
                >
                  {/* Buy / Rent sub-toggle */}
                  <div className="inline-flex rounded-full border border-white/25 bg-white/10 backdrop-blur-2xl p-1 mb-3" role="tablist" aria-label="Buy or Rent">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={intent === "buy"}
                      onClick={() => setIntent("buy")}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        intent === "buy" ? "bg-sg-gold text-sg-navy" : "text-white/80 hover:text-white"
                      }`}
                      data-testid="landing-intent-buy"
                    >
                      Buy
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={intent === "rent"}
                      onClick={() => setIntent("rent")}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        intent === "rent" ? "bg-sg-gold text-sg-navy" : "text-white/80 hover:text-white"
                      }`}
                      data-testid="landing-intent-rent"
                    >
                      Rent
                    </button>
                  </div>

                  {/* Filters row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select
                      value={locality}
                      onChange={(e) => setLocality(e.target.value)}
                      className="h-12 rounded-xl bg-white/10 backdrop-blur-2xl text-white text-sm px-4 focus:outline-none appearance-none"
                      style={{ border: "2px solid #B8960C" }}
                      data-testid="landing-search-locality"
                      aria-label="Location"
                    >
                      <option value="" className="bg-sg-navy text-white">Any location</option>
                      {allLocalities.map((l) => (
                        <option key={l} value={l} className="bg-sg-navy text-white">{l}</option>
                      ))}
                    </select>
                    <select
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      className="h-12 rounded-xl bg-white/10 backdrop-blur-2xl text-white text-sm px-4 focus:outline-none appearance-none"
                      style={{ border: "2px solid #B8960C" }}
                      data-testid="landing-search-budget"
                      aria-label="Budget"
                    >
                      <option value="" className="bg-sg-navy text-white">Any budget</option>
                      {BUDGETS.map((b) => (
                        <option key={b.value} value={b.value} className="bg-sg-navy text-white">{b.label}</option>
                      ))}
                    </select>
                    <select
                      value={bhk}
                      onChange={(e) => setBhk(e.target.value)}
                      className="h-12 rounded-xl bg-white/10 backdrop-blur-2xl text-white text-sm px-4 focus:outline-none appearance-none"
                      style={{ border: "2px solid #B8960C" }}
                      data-testid="landing-search-bhk"
                      aria-label="BHK"
                    >
                      <option value="" className="bg-sg-navy text-white">Any BHK</option>
                      {BHKS.map((b) => (
                        <option key={b} value={b} className="bg-sg-navy text-white">{b}</option>
                      ))}
                    </select>
                  </div>

                  {/* Keyword + CTA row */}
                  <div
                    className="mt-2 flex items-center gap-2 rounded-2xl bg-white/10 backdrop-blur-2xl pl-5 pr-2 py-2 shadow-2xl shadow-sg-navy/25"
                    style={{ border: "2px solid #B8960C" }}
                  >
                    <Search className="w-5 h-5 text-white/60 flex-shrink-0" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={intent === "rent" ? "Search locality or building (optional)" : "Search project or RERA number (optional)"}
                      className="flex-1 ml-2 h-12 bg-transparent text-white text-base placeholder:text-white/50 focus:outline-none"
                      data-testid="landing-search-input"
                    />
                    <button
                      type="submit"
                      className="h-12 px-4 sm:px-5 md:px-6 rounded-xl bg-sg-gold hover:bg-sg-gold-deep text-sg-navy text-sm font-semibold inline-flex items-center gap-2 transition-colors whitespace-nowrap"
                      data-testid="landing-search-submit"
                    >
                      <span className="sm:hidden">{intent === "rent" ? "Rent" : "Search"}</span>
                      <span className="hidden sm:inline">{intent === "rent" ? "Find Rentals" : "Search Verified Properties"}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key="agent-value-prop"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-6 max-w-3xl rounded-2xl border border-sg-gold/40 bg-white/8 backdrop-blur-2xl p-6 md:p-8 shadow-2xl shadow-sg-navy/30"
                  data-testid="landing-agent-value-prop"
                >
                  <div className="overline text-sg-gold mb-4">FOR REAL ESTATE PROFESSIONALS</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[
                      { icon: Users, title: "Lead Management", body: "AI Gatekeeper filters time-wasters. Only ready-to-buy leads reach you." },
                      { icon: Megaphone, title: "Marketing Studio", body: "One-click brochures, floor plans, WhatsApp posts — auto-branded to you." },
                      { icon: Layers, title: "Branded Portal", body: "Your own inpropportal.com/agent/{you} — polished, RERA-tagged, mobile-first." },
                    ].map(({ icon: Icon, title, body }) => (
                      <div key={title} className="rounded-xl border border-white/15 bg-white/5 p-4">
                        <div className="w-10 h-10 rounded-lg bg-sg-gold/15 border border-sg-gold/30 flex items-center justify-center mb-3">
                          <Icon className="w-5 h-5 text-sg-gold" />
                        </div>
                        <div className="text-white font-semibold text-sm">{title}</div>
                        <p className="mt-1 text-xs text-white/70 leading-relaxed">{body}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="text-xs text-white/60">
                      No setup fees · Cancel any time · Pune-first, India-ready
                    </div>
                    <Link
                      to="/subscribe"
                      className="h-12 px-6 rounded-xl bg-sg-gold hover:bg-sg-gold-deep text-sg-navy text-sm font-semibold inline-flex items-center gap-2 transition-colors shadow-lg shadow-sg-gold/30"
                      data-testid="landing-agent-cta"
                    >
                      Start for ₹999/month <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Subtle scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 text-[10px] uppercase tracking-[0.28em]"
        >
          Scroll to explore
        </motion.div>
      </section>

      {/* ═══════════════ TOI AI QUOTIENT AWARDS · NOMINEE BADGE ═══════════════ */}
      <section className="bg-sg-paper py-8 border-b border-sg-line" data-testid="landing-toi-aiq-badge">
        <div className="relative overflow-hidden">
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_pune-property-ai/artifacts/7yj6lltp_TIO%20AIQ%202026.png"
            alt="TOI AI Quotient Awards 2026 — Nominee"
            className="w-[128px] h-auto animate-marquee-single"
            loading="lazy"
            decoding="async"
          />
          <p className="text-xs text-sg-mute text-center mt-3">Nominee · TOI AI Quotient Awards 2026</p>
        </div>
      </section>

      {/* ═══════════════ 4. TOP CORRIDORS (moved above Launch Roadmap) ═══════════════ */}
      {trends.length > 0 && (
        <section className="py-24 lg:py-28 bg-white border-b border-sg-line" data-testid="landing-top-areas">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="flex items-baseline justify-between mb-12">
                <div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="overline text-sg-gold-deep">FEATURED CITY · TOP CORRIDORS</div>
                    <MicroStat label="Tracked" value={`${trends.length} corridors`} />
                    <MicroStat label="Top YoY" value={trends[0]?.yoy || "—"} />
                  </div>
                  <h2 className="mt-4 font-hero text-4xl lg:text-5xl text-sg-navy font-semibold leading-[1.05]">
                    Where Pune capital<br className="hidden md:block" />
                    <span className="font-hero-italic text-sg-gold-deep font-normal"> is compounding.</span>
                  </h2>
                </div>
                <Link to="/buy" className="hidden md:inline-flex items-center gap-2 text-sm text-sg-navy hover:text-sg-gold-deep" data-testid="landing-topareas-all">
                  All localities <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden -mx-6 lg:mx-0" data-testid="landing-locality-scroller">
                <div className="flex gap-4 w-max animate-marquee-x px-6 lg:px-0">
                  {trends.slice(0, 6).map((t) => <LocalityChip key={t.locality} trend={t} />)}
                  {trends.slice(0, 6).map((t) => <LocalityChip key={`${t.locality}-dup`} trend={t} ariaHidden />)}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ═══════════════ 2. INTERACTIVE LAUNCH ROADMAP ═══════════════ */}
      <section className="relative py-24 lg:py-28 border-b border-sg-line bg-white bg-technical-grid" data-testid="landing-roadmap">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <div className="flex items-center gap-4 mb-3 flex-wrap">
                <div className="overline text-sg-gold-deep">LAUNCH ROADMAP · INDIA</div>
                <MicroStat label="Coverage" value="1 / 8 cities" />
                <MicroStat label="Active" value="Pune West + East" />
              </div>
              <h2 className="mt-4 font-hero text-4xl lg:text-5xl text-sg-navy font-semibold leading-[1.05]">
                Live in Pune. <span className="font-hero-italic text-sg-gold-deep font-normal">Coming to India,</span> city by city.
              </h2>
              <p className="mt-4 text-sm text-sg-mute leading-relaxed">
                Hover a city to preview the launch window and local RERA framework. We onboard verified local advisors before every launch — never the other way round.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <LaunchRoadmap />
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ 3. FEATURED CITY · PUNE — INSIGHTS (STATS BAND) ═══════════════ */}
      {/* Sits directly under the Launch Roadmap with tight top padding so the
          numbers feel like a continuation, not a separate section. */}
      <section className="bg-sg-cream-soft/70 pt-14 pb-24 lg:pt-16 lg:pb-28 border-b border-sg-line" data-testid="landing-insights">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-baseline justify-between mb-10 flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="overline text-sg-gold-deep">FEATURED CITY · PUNE</div>
                  <MicroStat label="Refreshed" value="hourly" />
                  <MicroStat label="Load" value="0.8s p95" />
                </div>
                <h2 className="mt-4 font-hero text-4xl lg:text-5xl text-sg-navy font-semibold leading-[1.05]">
                  Pune&apos;s most<br className="hidden md:block" />
                  <span className="font-hero-italic text-sg-gold-deep font-normal"> verified property network.</span>
                </h2>
                <p className="mt-4 text-sm text-sg-mute leading-relaxed max-w-2xl" data-testid="landing-insights-subtext">
                  Every project is MahaRERA-registered. Every agent is identity-verified.
                  No ghost listings, no cold leads.
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-sg-gold-deep hidden md:block" />
            </div>
          </Reveal>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden -mx-6 lg:-mx-8"
            data-testid="landing-insights-marquee"
          >
            <div className="flex gap-4 w-max animate-marquee-x px-6 lg:px-8">
              {[...insightsTiles, ...insightsTiles].map((t, i) => (
                <Link
                  key={`${t.label}-${i}`}
                  to={t.href}
                  className="group block flex-shrink-0 w-64 lg:w-72"
                  aria-hidden={i >= insightsTiles.length ? true : undefined}
                  tabIndex={i >= insightsTiles.length ? -1 : undefined}
                >
                  <div
                    className="h-full bg-white rounded-2xl px-6 py-8 hover:shadow-lg hover:shadow-sg-gold/5 transition-all"
                    style={{ border: "3px solid #B8960C" }}
                    data-testid={`landing-stat-${t.label.toLowerCase().replace(/\s/g,'-')}`}
                  >
                    <div className="font-hero text-4xl lg:text-5xl text-sg-navy tracking-tight leading-none font-semibold group-hover:text-sg-gold-deep transition-colors">
                      {t.value}
                    </div>
                    <div className="mt-6 text-[8px] tracking-[0.24em] uppercase text-sg-mute font-semibold">
                      {t.label}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ 3b. REGISTERED AGENTS RAIL (agent audience only) ═══════════════ */}
      {isAgentView && (
        <section
          id="landing-agents-grid"
          className="py-20 lg:py-24 bg-white border-b border-sg-line"
          data-testid="landing-agents-rail"
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="max-w-2xl mb-10">
                <div className="flex items-center gap-4 mb-3 flex-wrap">
                  <div className="overline text-sg-gold-deep">MEET THE AGENTS</div>
                  <MicroStat label="Live" value={`${agentsCount ?? "—"} registered`} />
                  <MicroStat label="Refresh" value="every 10 min" />
                </div>
                <h2 className="mt-4 font-hero text-4xl lg:text-5xl text-sg-navy font-semibold leading-[1.05]">
                  Every card here is a <span className="font-hero-italic text-sg-gold-deep font-normal">MahaCRITI-registered</span> professional.
                </h2>
                <p className="mt-4 text-sm text-sg-mute leading-relaxed">
                  Only agents on an active subscription or live free-trial appear below —
                  the list refreshes every ten minutes. Tap any card to WhatsApp them directly.
                </p>
              </div>
            </Reveal>

            {agents.length === 0 ? (
              <div className="sg-card-cream p-10 text-center max-w-xl mx-auto" data-testid="landing-agents-empty">
                <Users className="w-10 h-10 mx-auto text-sg-gold-deep" />
                <h3 className="font-display text-2xl text-sg-navy mt-3">
                  Onboarding drops soon.
                </h3>
                <p className="text-sm text-sg-mute mt-2">
                  Our active-agent roster refreshes every 10 min. Check back shortly, or join the platform yourself below.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="landing-agents-grid">
                {agents.map((a) => (
                  <AgentDirectoryCard key={a.slug} agent={a} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══════════════ 5. MASTER PROJECTS · DENSE LIST ═══════════════ */}
      {projects.length > 0 && (
        <section className="relative border-y border-sg-line bg-white bg-technical-dots py-24 lg:py-28" data-testid="landing-projects">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <div className="flex items-baseline justify-between mb-12 flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="overline text-sg-gold-deep">FEATURED CITY · VERIFIED PROJECTS</div>
                    <MicroStat label="Total tracked" value={String(stats.projects_count || projects.length)} />
                    <MicroStat label="Verified" value="100%" />
                  </div>
                  <h2 className="mt-4 font-hero text-4xl lg:text-5xl text-sg-navy font-semibold leading-[1.05]">
                    New launches,<br className="hidden md:block" />
                    <span className="font-hero-italic text-sg-gold-deep font-normal"> curated.</span>
                  </h2>
                </div>
                <Link to="/buy?property_source=master_projects" className="hidden md:inline-flex items-center gap-2 text-sm text-sg-navy hover:text-sg-gold-deep" data-testid="landing-projects-all">
                  Browse all <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
            <motion.div
              variants={staggerParent}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {projects.map((p) => (
                <motion.div key={p.project_id} variants={revealVariants}>
                  <ProjectCard
                    project={p}
                    tag="NEW LAUNCH"
                    testIdPrefix="landing-project"
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══════════════ 6. BENTO GRID · NATIONAL BROKER NETWORK ═══════════════ */}
      <section className="relative py-24 lg:py-32 bg-sg-paper bg-technical-grid" data-testid="landing-agent-value">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="overline text-sg-gold-deep">NATIONAL BROKER NETWORK</div>
                <MicroStat label="Onboarding" value="Pune first" />
                <MicroStat label="Fee" value="₹999/mo · flat" />
              </div>
              <h2 className="mt-4 font-hero text-4xl lg:text-6xl text-sg-navy font-semibold leading-[1.02]">
                Ship your practice,<br />
                <span className="font-hero-italic text-sg-gold-deep font-normal">not spreadsheets.</span>
              </h2>
              <p className="mt-5 text-base text-sg-mute leading-relaxed">
                One production-grade platform replaces the four tools every Indian real-estate broker cobbles together — site, CRM, marketing, RERA badge. Rolling out city by city, professional by professional.
              </p>
            </div>
          </Reveal>

          <motion.div
            variants={staggerParent}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="grid grid-cols-1 md:grid-cols-6 gap-4"
          >
            {/* Big anchor cell — the star benefit */}
            <BentoCell accent className="md:col-span-4 md:row-span-2 p-10 lg:p-12 flex flex-col justify-between min-h-[380px]" testId="landing-agent-benefit-rera-verified">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-sg-gold/40 bg-sg-gold/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-sg-gold font-semibold">
                  <ShieldCheck className="w-3 h-3" /> Signature feature
                </div>
                <h3 className="mt-8 font-hero text-3xl lg:text-5xl leading-tight font-semibold">
                  RERA-authorised, state<br className="hidden md:block" />
                  <span className="font-hero-italic text-sg-gold font-normal">by state.</span>
                </h3>
              </div>
              <div>
                <p className="text-white/70 text-sm max-w-md leading-relaxed">
                  Your state-specific RERA number renders on every listing card, project page, and social post automatically — buyers see a credential, not a chat prompt.
                </p>
                <Link
                  to="/register?role=agent"
                  className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-sg-gold text-sg-navy font-semibold text-sm hover:bg-white transition-colors"
                  data-testid="landing-agent-cta-bento"
                >
                  Join the network · ₹999/mo
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="pointer-events-none absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-sg-gold/10 blur-3xl" />
            </BentoCell>

            {/* Top-right — Marketing Studio */}
            <BentoCell className="md:col-span-2 p-8 min-h-[180px] flex flex-col justify-between" testId="landing-agent-benefit-ai-marketing-studio">
              <div className="w-10 h-10 rounded-xl bg-sg-cream-soft border border-sg-gold/40 flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-sg-gold-deep" />
              </div>
              <div>
                <div className="font-hero text-xl font-semibold">AI Marketing Studio</div>
                <div className="mt-2 text-xs text-sg-mute leading-relaxed">
                  GPT captions + branded graphics, published to FB &amp; IG.
                </div>
              </div>
            </BentoCell>

            {/* Right-middle — Lead capture */}
            <BentoCell className="md:col-span-2 p-8 min-h-[180px] flex flex-col justify-between" testId="landing-agent-benefit-lead-capture">
              <div className="w-10 h-10 rounded-xl bg-sg-cream-soft border border-sg-gold/40 flex items-center justify-center">
                <Users className="w-4 h-4 text-sg-gold-deep" />
              </div>
              <div>
                <div className="font-hero text-xl font-semibold">Lead capture</div>
                <div className="mt-2 text-xs text-sg-mute leading-relaxed">
                  Buyer &amp; seller enquiries with your branding, direct to inbox.
                </div>
              </div>
            </BentoCell>

            {/* Bottom-left of second row — Listing tools */}
            <BentoCell className="md:col-span-3 p-8 min-h-[180px] flex flex-col justify-between" testId="landing-agent-benefit-listing-tools">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sg-cream-soft border border-sg-gold/40 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-sg-gold-deep" />
                </div>
                <div className="font-hero text-xl font-semibold">Zero-form listing tools</div>
              </div>
              <div className="text-xs text-sg-mute leading-relaxed">
                WhatsApp paste · PDF Magic-Drop · CSV import — the fastest inventory intake in the industry.
              </div>
            </BentoCell>

            {/* Bottom-right — Master DB */}
            <BentoCell className="md:col-span-3 p-8 min-h-[180px] flex flex-col justify-between" testId="landing-agent-benefit-master-projects">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sg-cream-soft border border-sg-gold/40 flex items-center justify-center">
                  <Layers className="w-4 h-4 text-sg-gold-deep" />
                </div>
                <div className="font-hero text-xl font-semibold">Curated master-project DB</div>
              </div>
              <div className="text-xs text-sg-mute leading-relaxed">
                Every builder launch verified once by the platform, attributed automatically to your listings.
              </div>
            </BentoCell>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ 7. FOOTER STRIP ═══════════════ */}
      <section className="border-t border-sg-line py-12 bg-white" data-testid="landing-footer-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-4 h-4 text-sg-gold-deep" />
            <span className="text-xs text-sg-mute">RERA-authorised advisors · Verified listings · Multi-state rollout in progress</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-sg-mute">
            <a
              href="https://www.instagram.com/INPropPortal"
              target="_blank"
              rel="noreferrer"
              aria-label="INPropPortal on Instagram"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-sg-line text-sg-mute hover:text-sg-navy hover:border-sg-gold-deep transition-colors"
              data-testid="landing-footer-social-instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <Link to="/contact" className="hover:text-sg-navy underline underline-offset-4 decoration-sg-gold/40 hover:decoration-sg-gold-deep">Contact</Link>
            <span className="text-sg-line">·</span>
            <Link to="/privacy" className="hover:text-sg-navy underline underline-offset-4 decoration-sg-gold/40 hover:decoration-sg-gold-deep">Privacy</Link>
            <span className="text-sg-line">·</span>
            <Link to="/terms" className="hover:text-sg-navy underline underline-offset-4 decoration-sg-gold/40 hover:decoration-sg-gold-deep">Terms</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
